export type ListItem = {
  id: unknown;
  sourceAttribute?: unknown;
  pathPreview?: unknown;
  kind?: unknown;
  leafSource?: unknown;
  pool?: { key: string; source?: string } | null;
  values?: { match: string; ignored?: boolean }[] | null;
  sameBoxAs?: string;
  warnings?: string[] | null;
};

// ponytail: waits for warning codes on the item. Intent: hide the new-pool warning. Breaks if the engine rewords it.
const NEW_POOL_WARNING = "new pool suggested";
const UNCHECKED_MATCHES = ["ai", "new", "not checked"];

export function buildListLine(item: ListItem) {
  const id = String(item.id);
  const title = `${item.sourceAttribute ?? id} (${id})`;
  const target = item.pathPreview
    ? [item.pathPreview, item.kind, item.pool && `pool: ${item.pool.key}`]
        .filter(Boolean)
        .join("   ")
    : "—";
  const toCheck = (item.values ?? []).filter(
    (v) => !v.ignored && UNCHECKED_MATCHES.includes(v.match),
  ).length;
  const marks = [
    toCheck ? `⚠ ${toCheck} value${toCheck === 1 ? "" : "s"} to check` : "",
    item.leafSource === "new" ? "✚ new leaf" : "",
    item.sameBoxAs ? `1 box with ${item.sameBoxAs}` : "",
    ...(item.warnings ?? [])
      .filter((warning) => warning !== NEW_POOL_WARNING)
      .map((warning) => `⚠ ${warning}`),
  ].filter(Boolean);
  return { title, target, marks };
}

export function countNewLists(rows: { pool?: { source?: string } | null }[]) {
  return rows.filter((row) => row.pool?.source === "new").length;
}

export function withSameBoxAs<
  T extends { id?: unknown; pathPreview?: unknown },
>(rows: T[]): T[] {
  const idsByPath = new Map<unknown, string[]>();
  rows.forEach((row) => {
    if (row.pathPreview)
      idsByPath.set(row.pathPreview, [
        ...(idsByPath.get(row.pathPreview) ?? []),
        String(row.id),
      ]);
  });
  return rows.map((row) => ({
    ...row,
    sameBoxAs: (idsByPath.get(row.pathPreview) ?? []).find(
      (id) => id !== String(row.id),
    ),
  }));
}
